import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ActionType } from 'typesafe-actions'
import { restfulErrorEvent } from 'utils/restful'
import { NAMESPACE } from './constants'
import { TTpnAutoSendRequest, TTpnSendResult, TTpnSettings, TTpnSettingsRequest, TTpnState } from './types'

const initialState: TTpnState = {
  settings: null,
  loaded: false,
  loadFailed: false,
  lastSendResult: null,
  saveError: null,
}

/** `http_error` minted by `restful.catchRestError` for the given request. */
const isHttpErrorFor =
  (requestType: string) =>
  (action: AnyAction): boolean =>
    action.type === restfulErrorEvent.type && action.meta?.initAction?.type === `${NAMESPACE}/${requestType}`

const isSaveHttpError = isHttpErrorFor('saveTpnSettings_request')
const isFetchHttpError = isHttpErrorFor('tpnSettings_request')

const tpnApiSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    tpnSettings_request: () => {},
    tpnSettings_success: (state, { payload }: PayloadAction<TTpnSettings | null>) => {
      state.settings = payload
      state.loaded = true
      state.loadFailed = false
    },
    saveTpnSettings_request: {
      reducer: state => {
        state.saveError = null
      },
      prepare: (payload: TTpnSettingsRequest) => ({ payload }),
    },
    saveTpnSettings_success: state => {
      state.saveError = null
    },
    clearTpnSaveError: state => {
      state.saveError = null
    },
    removeTpnSettings_request: () => {},
    removeTpnSettings_success: state => {
      state.settings = null
      state.lastSendResult = null
    },
    setTpnAutoSend_request: {
      reducer: () => {},
      prepare: (payload: TTpnAutoSendRequest) => ({ payload }),
    },
    // Flip in place so the switch settles before the refetch lands.
    setTpnAutoSend_success: (state, { payload }: PayloadAction<TTpnAutoSendRequest>) => {
      if (state.settings) {
        state.settings.autoSend = payload.enabled
      }
    },
    sendTpnReport_request: () => {},
    sendTpnReport_success: (state, { payload }: PayloadAction<TTpnSendResult>) => {
      state.lastSendResult = payload
    },
  },
  extraReducers: builder => {
    // le-go answers `{ message }` (echo HTTPError). Kept here so the connect
    // modal can show it inline and keep its button in the error state.
    builder.addMatcher(isSaveHttpError, (state, action: AnyAction) => {
      state.saveError = action.payload?.response?.message ?? 'Unable to connect TPN. Please try again.'
    })
    // A failed fetch still counts as "loaded" so the card is not blank while
    // insights is down, but `loadFailed` keeps it from reading "Not connected"
    // and offering Connect when the agency may well be connected.
    builder.addMatcher(isFetchHttpError, state => {
      state.loaded = true
      state.loadFailed = true
    })
  },
})

const { reducer, actions: events } = tpnApiSlice

export type TpnEvent = ActionType<typeof events>

export { events, initialState, reducer }
